import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { QUICK_ADD_MODULE, type QuickAddActionId } from "@/lib/quick-add-permissions";
import {
  User,
  MessageSquare,
  Mail,
  DollarSign,
  Calendar,
  Music,
  Megaphone,
  FolderKanban,
  type LucideIcon,
} from "lucide-react";

interface QuickAddPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAction: (action: string) => void;
  onCloseAutoFocus?: (event: Event) => void;
}

interface QuickAddAction {
  id: QuickAddActionId;
  icon: LucideIcon;
  title: string;
  description: string;
  shortcut: string;
}

const actions: QuickAddAction[] = [
  {
    id: "add-person",
    icon: User,
    title: "Add Person",
    description: "Add someone to your community",
    shortcut: "N",
  },
  {
    id: "create-request",
    icon: MessageSquare,
    title: "Create Request",
    description: "Log a care request or follow-up",
    shortcut: "R",
  },
  {
    id: "record-contribution",
    icon: DollarSign,
    title: "Record Contribution",
    description: "Enter a gift or donation",
    shortcut: "C",
  },
  {
    id: "create-event",
    icon: Calendar,
    title: "Create Event",
    description: "Schedule a one-time event",
    shortcut: "E",
  },
  {
    id: "create-service",
    icon: Music,
    title: "Create Service",
    description: "Build a worship service plan",
    shortcut: "S",
  },
  {
    id: "post-announcement",
    icon: Megaphone,
    title: "Post Announcement",
    description: "Share with your community",
    shortcut: "A",
  },
  {
    id: "create-project",
    icon: FolderKanban,
    title: "Create Project",
    description: "Start a new project or initiative",
    shortcut: "P",
  },
  {
    id: "create-message",
    icon: Mail,
    title: "Create Message",
    description: "Compose and send a message",
    shortcut: "M",
  },
] as const;

export function QuickAddPanel({ open, onOpenChange, onAction, onCloseAutoFocus }: QuickAddPanelProps) {
  const { can } = usePermissions();

  // Only offer actions the current role is allowed to perform. A self-service
  // member, for example, can file requests but must not see "Add Person" or
  // "Record Contribution". This mirrors the backend RLS rather than relying on
  // it to reject the write after the user has already opened the form.
  const visibleActions = actions.filter((action) => can(QUICK_ADD_MODULE[action.id], "add"));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[440px] p-0 gap-0" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogTitle className="sr-only">Quick Actions</DialogTitle>
        <DialogDescription className="sr-only">
          Choose a permitted quick action to create a person, request, contribution, event, service, announcement, project, or message.
        </DialogDescription>
        <div className="flex flex-col py-2">
          {visibleActions.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              No quick actions are available for your role.
            </p>
          )}
          {visibleActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                type="button"
                className={cn(
                  "flex items-center gap-3 px-4 py-3 text-left transition-colors",
                  "hover:bg-primary-tint hover:border-primary-tint-border",
                  "focus-visible:outline-none focus-visible:bg-primary-tint"
                )}
                onClick={() => {
                  onAction(action.id);
                  onOpenChange(false);
                }}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-tint">
                  <Icon className="h-5 w-5 text-primary-tint-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium leading-tight">{action.title}</div>
                  <div className="text-xs text-muted-foreground leading-tight mt-0.5">
                    {action.description}
                  </div>
                </div>
                <kbd className="ml-auto shrink-0 rounded-md border border-border bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                  {action.shortcut}
                </kbd>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
